import {
    addDays,
    addMonths,
    endOfMonth,
    isAfter,
    isSameDay,
    startOfMonth,
    subDays,
    subMonths,
} from 'date-fns';
import { ReportDateTimeframe } from 'c-reports/Types';

type DateRange = [Date, Date];

// The navigation the report date switcher offers, as plain date maths. A timeframe without
// stepping (Range, All) has nothing to jump to, so those fall back to today.

/** The window "go to start" jumps to: the first period from the earliest date. */
export const firstRange = (timeframe: ReportDateTimeframe, minDate: Date): DateRange => {
    if (timeframe === ReportDateTimeframe.Daily) {
        return [minDate, minDate];
    }
    if (timeframe === ReportDateTimeframe.Weekly) {
        return [minDate, addDays(minDate, 7)];
    }
    if (timeframe === ReportDateTimeframe.BiWeekly) {
        return [minDate, addDays(minDate, 14)];
    }
    if (timeframe === ReportDateTimeframe.Monthly) {
        return [startOfMonth(minDate), endOfMonth(minDate)];
    }
    return [new Date(), new Date()];
};

/** The window "go to end" jumps to: the last period up to the latest date. */
export const lastRange = (timeframe: ReportDateTimeframe, maxDate?: Date | null): DateRange => {
    if (!maxDate) return [new Date(), new Date()];
    if (timeframe === ReportDateTimeframe.Daily) {
        return [maxDate, maxDate];
    }
    if (timeframe === ReportDateTimeframe.Weekly) {
        return [subDays(maxDate, 7), maxDate];
    }
    if (timeframe === ReportDateTimeframe.BiWeekly) {
        return [subDays(maxDate, 14), maxDate];
    }
    if (timeframe === ReportDateTimeframe.Monthly) {
        return [startOfMonth(maxDate), endOfMonth(maxDate)];
    }
    return [new Date(), new Date()];
};

/** The period before the one shown. */
export const previousRange = (
    timeframe: ReportDateTimeframe,
    startDate: Date,
    endDate: Date,
): DateRange => {
    if (timeframe === ReportDateTimeframe.Daily) {
        return [subDays(startDate, 1), subDays(startDate, 1)];
    }
    if (timeframe === ReportDateTimeframe.Weekly) {
        return [subDays(startDate, 7), subDays(endDate, 7)];
    }
    if (timeframe === ReportDateTimeframe.BiWeekly) {
        return [subDays(startDate, 14), subDays(endDate, 14)];
    }
    if (timeframe === ReportDateTimeframe.Monthly) {
        const newStart = subMonths(startDate, 1);
        return [newStart, endOfMonth(newStart)];
    }
    return [new Date(), new Date()];
};

/** The period after the one shown; null for a timeframe that doesn't step. */
export const nextRange = (
    timeframe: ReportDateTimeframe,
    startDate: Date,
    endDate: Date,
): DateRange | null => {
    if (timeframe === ReportDateTimeframe.Daily) {
        return [addDays(startDate, 1), addDays(startDate, 1)];
    }
    if (timeframe === ReportDateTimeframe.Weekly) {
        return [addDays(startDate, 7), addDays(endDate, 7)];
    }
    if (timeframe === ReportDateTimeframe.BiWeekly) {
        return [addDays(startDate, 14), addDays(endDate, 14)];
    }
    if (timeframe === ReportDateTimeframe.Monthly) {
        const newStart = addMonths(startDate, 1);
        return [newStart, endOfMonth(newStart)];
    }
    return null;
};

/** Going back is allowed while the previous period still reaches the earliest date. */
export const isPreviousDisabled = (
    timeframe: ReportDateTimeframe,
    minDate: Date | null | undefined,
    previous: DateRange,
): boolean => {
    if (!minDate) return true;
    if (timeframe === ReportDateTimeframe.All) return true;
    if (isSameDay(previous[0], minDate)) return false;
    if (isAfter(previous[1], minDate) || isSameDay(previous[1], minDate)) return false;

    return true;
};

/** Going forward stops once the period shown reaches the latest date. */
export const isNextDisabled = (
    timeframe: ReportDateTimeframe,
    maxDate: Date | null | undefined,
    endDate: Date,
): boolean => {
    if (timeframe === ReportDateTimeframe.All) return true;
    if (!maxDate) return true;
    return isSameDay(endDate, maxDate) || isAfter(endDate, maxDate);
};
